import {
  RiMoreLine,
  RiFileCopyLine,
  RiLockUnlockLine,
  RiDeleteBinLine,
} from '@remixicon/react';

import { boundingRect, PageContext } from '@lidojs/design-core';
import React, { Fragment, useContext, useMemo, useRef } from 'react';
import { useEditor, useSelectedLayers } from '../hooks';
import { isGroupLayer } from '../ultils/layer/layers';
import { duplicate } from '../ultils/menu-actions/duplicate';

const Toolbar = () => {
  const { pageIndex } = useContext(PageContext);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const { selectedLayerIds, selectedLayers } = useSelectedLayers();
  const {
    actions,
    state,
    isDragging,
    isResizing,
    isRotating,
    controlBox,
    pageSize,
    isOpenMenu,
    scale,
    isPageLocked,
    isUpdatingLine,
  } = useEditor((state, query) => ({
    isGroup: state.selectedLayers[state.activePage].length > 1,
    isDragging: state.dragData.status,
    isResizing: state.resizeData.status,
    isRotating: state.rotateData.status,
    isUpdatingLine: state.updateLineData.status,
    controlBox: state.controlBox,
    pageSize: query.getPageSize(),
    isPageLocked: state.pages[state.activePage].layers.ROOT.data.locked,
    isOpenMenu: !!state.openMenu,
    scale: state.scale,
  }));
  const isLocked = selectedLayers.find((i) => i.data.locked);
  const boundingBoxRect = useMemo(() => {
    if (!controlBox) {
      return {
        x: 0,
        y: 80,
        width: pageSize.width,
        height: pageSize.height,
      };
    }
    return boundingRect(
      controlBox.boxSize,
      controlBox.position,
      controlBox.rotate
    );
  }, [controlBox, pageSize.height, pageSize.width]);
  const handleDuplicate = () => {
    duplicate(state, { pageIndex, layerIds: selectedLayerIds, actions });
  };
  const showContextMenu = () => {
    if (isOpenMenu) {
      actions.hideContextMenu();
    } else {
      const rect = toolbarRef.current?.getBoundingClientRect() as DOMRect;
      actions.showContextMenu({
        clientX: rect.right - 42,
        clientY: rect.bottom + 4,
      });
    }
  };

  const handleUngroup = () => {
    if (selectedLayerIds.length === 1) {
      actions.ungroup(selectedLayerIds[0]);
    }
  };

  const handleGroup = () => {
    actions.group(selectedLayerIds);
  };
  if (
    isDragging ||
    isResizing ||
    isRotating ||
    isUpdatingLine ||
    (selectedLayerIds.includes('ROOT') && !isLocked && !isPageLocked) ||
    !controlBox
  ) {
    return null;
  }
  const containerGroupLayer = !!selectedLayers.find((l) => isGroupLayer(l));
  return (
    <div
      ref={toolbarRef}
      css={(theme) => ({
        position: 'absolute',
        left: (boundingBoxRect.x + boundingBoxRect.width / 2) * scale,
        top: boundingBoxRect.y * scale - 60,
        transform: 'translateX(-50%)',
      })}
    >
      <div
        css={(theme) => ({
          height: 40,
          borderRadius: 4,
          padding: '0 4px',
          display: 'inline-flex',
          alignItems: 'center',
          background: theme.colors.background,
          boxShadow:
            '0 0 0 1px rgba(64,87,109,.07),0 2px 12px rgba(53,71,90,.2)',
          overflow: 'hidden',
          pointerEvents: 'auto',
          color: theme.colors.content,
        })}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          css={(theme) => ({
            alignItems: 'center',
            display: 'flex',
            whiteSpace: 'nowrap',
          })}
        >
          {!isPageLocked && !isLocked && !selectedLayerIds.includes('ROOT') && (
            <Fragment>
              {selectedLayerIds.length > 1 && (
                <div
                  css={(theme) => ({
                    lineHeight: '32px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: 14,
                    padding: '0 8px',
                    fontWeight: 700,
                    ':hover': {
                      backgroundColor: theme.colors.defaultButtonActive,
                    },
                  })}
                  onClick={handleGroup}
                >
                  Group
                </div>
              )}
              {containerGroupLayer && (
                <div
                  css={(theme) => ({
                    lineHeight: '32px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: 14,
                    padding: '0 8px',
                    fontWeight: 700,
                    ':hover': {
                      backgroundColor: theme.colors.defaultButtonActive,
                    },
                  })}
                  onClick={handleUngroup}
                >
                  Ungroup
                </div>
              )}
              <div
                css={(theme) => ({
                  width: 32,
                  height: 32,
                  color: theme.colors.content,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 24,
                  ':hover': {
                    backgroundColor: theme.colors.defaultButtonActive,
                  },
                })}
                onClick={handleDuplicate}
              >
                <RiFileCopyLine size={18} />
              </div>

              <div
                css={(theme) => ({
                  width: 32,
                  height: 32,
                  color: theme.colors.content,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 24,
                  ':hover': {
                    backgroundColor: theme.colors.defaultButtonActive,
                  },
                })}
                onClick={() => actions.deleteLayer(pageIndex, selectedLayerIds)}
              >
                <RiDeleteBinLine size={18} />
              </div>

              <div
                css={(theme) => ({
                  width: 32,
                  height: 32,
                  color: theme.colors.content,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 24,
                  ':hover': {
                    backgroundColor: theme.colors.defaultButtonActive,
                  },
                })}
                onClick={showContextMenu}
              >
                <RiMoreLine size={18} />
              </div>
            </Fragment>
          )}
          {(isLocked || isPageLocked) && (
            <div
              css={(theme) => ({
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: isPageLocked ? 'default' : 'pointer',
                fontSize: 24,
                color: theme.colors.content,
                opacity: isPageLocked ? '0.5' : 1,
                ':hover': {
                  backgroundColor: isPageLocked
                    ? undefined
                    : theme.colors.defaultButtonActive,
                  color: isPageLocked ? undefined : '#1B1B1B',
                },
              })}
              onClick={() => {
                actions.unlock(pageIndex, selectedLayerIds);
              }}
            >
              <RiLockUnlockLine size={20} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Toolbar);
